import React, { useState, useEffect } from 'react';
import API_CONFIG from '../utils/apiConfig';
import './CronLogsTab.css';
import '../styles/pagination-mobile.css';

const CronLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filters
  const [jobIdFilter, setJobIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Manual trigger state
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [jobIdFilter, statusFilter, page, limit]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      
      if (jobIdFilter) params.append('job_id', jobIdFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const url = API_CONFIG.getApiUrl(`/cron-jobs/logs?${params.toString()}`);
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalCount(data.total_count || 0);
        setTotalPages(Math.ceil((data.total_count || 0) / limit));
      } else {
        console.error('Failed to fetch cron logs');
        if (window.showToast) {
          window.showToast('Failed to fetch cron logs', 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching cron logs:', error);
      if (window.showToast) {
        window.showToast('Error fetching cron logs', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogDetail = async (logId) => {
    try {
      const url = API_CONFIG.getApiUrl(`/cron-jobs/logs/${logId}`);
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedLog(data);
        setShowDetailModal(true);
      } else {
        console.error('Failed to fetch log detail');
        if (window.showToast) {
          window.showToast('Failed to fetch log detail', 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching log detail:', error);
      if (window.showToast) {
        window.showToast('Error fetching log detail', 'error');
      }
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(0);
    return `${minutes}m ${secs}s`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString();
    } catch (e) {
      return isoString;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'success':
        return 'status-badge status-success';
      case 'failure':
        return 'status-badge status-failure';
      case 'running':
        return 'status-badge status-running';
      default:
        return 'status-badge';
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
  };

  const triggerJob = async (jobId, jobName) => {
    setTriggeringJob(jobId);
    try {
      const url = API_CONFIG.getApiUrl(`/cron-jobs/jobs/${jobId}/run`);
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'POST' });
      
      if (response.ok) {
        if (window.showToast) {
          window.showToast(`${jobName} triggered successfully!`, 'success');
        }
        // Refresh logs after a brief delay to show the new execution
        setTimeout(() => {
          fetchLogs();
        }, 1000);
      } else {
        const data = await response.json();
        if (window.showToast) {
          window.showToast(data.detail || 'Failed to trigger job', 'error');
        }
      }
    } catch (error) {
      console.error('Error triggering job:', error);
      if (window.showToast) {
        window.showToast('Error triggering job', 'error');
      }
    } finally {
      setTriggeringJob(null);
    }
  };

  const cleanupStuckJobs = async () => {
    if (!window.confirm('Clean up stuck jobs? This will mark all jobs running for >1 hour as failed.')) {
      return;
    }

    setCleaningUp(true);
    try {
      const url = API_CONFIG.getApiUrl('/cron-jobs/logs/cleanup-stuck');
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'POST' });
      
      if (response.ok) {
        const data = await response.json();
        if (window.showToast) {
          window.showToast(data.message || 'Cleanup completed', 'success');
        }
        // Refresh logs to show cleaned up jobs
        fetchLogs();
      } else {
        const data = await response.json();
        if (window.showToast) {
          window.showToast(data.detail || 'Failed to cleanup stuck jobs', 'error');
        }
      }
    } catch (error) {
      console.error('Error cleaning up stuck jobs:', error);
      if (window.showToast) {
        window.showToast('Error cleaning up stuck jobs', 'error');
      }
    } finally {
      setCleaningUp(false);
    }
  };

  return (
    <div className="cron-logs-container">
      <div className="cron-logs-header">
        <h2>Cron Job Execution Logs</h2>
        <div className="header-actions">
          <button 
            onClick={cleanupStuckJobs} 
            disabled={cleaningUp}
            className="cleanup-btn"
          >
            {cleaningUp ? '⏳ Cleaning...' : '🧹 Cleanup Stuck Jobs'}
          </button>
          <button onClick={fetchLogs} className="refresh-btn">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Manual Trigger Section */}
      <div className="manual-trigger-section">
        <h3>Manual Trigger</h3>
        <div className="trigger-buttons">
          <button 
            onClick={() => triggerJob('cis_notification', 'CIS Notification')}
            disabled={triggeringJob === 'cis_notification'}
            className="trigger-btn trigger-notification"
          >
            {triggeringJob === 'cis_notification' ? '⏳ Running...' : '📧 Trigger CIS Notification'}
          </button>
          <button 
            onClick={() => triggerJob('cis_auto_submission', 'CIS Auto-Submission')}
            disabled={triggeringJob === 'cis_auto_submission'}
            className="trigger-btn trigger-submission"
          >
            {triggeringJob === 'cis_auto_submission' ? '⏳ Running...' : '📤 Trigger CIS Auto-Submission'}
          </button>
          <button 
            onClick={() => triggerJob('cis_tax_month_boundary', 'Tax Month Boundary')}
            disabled={triggeringJob === 'cis_tax_month_boundary'}
            className="trigger-btn trigger-boundary"
          >
            {triggeringJob === 'cis_tax_month_boundary' ? '⏳ Running...' : '📅 Trigger Tax Month Boundary'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="cron-filters">
        <div className="filter-group">
          <label>Job:</label>
          <select value={jobIdFilter} onChange={(e) => { setJobIdFilter(e.target.value); setPage(1); }}>
            <option value="">All Jobs</option>
            <option value="cis_notification">CIS Notification</option>
            <option value="cis_auto_submission">CIS Auto-Submission</option>
            <option value="cis_tax_month_boundary">Tax Month Boundary</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
            <option value="running">Running</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Per Page:</label>
          <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="cron-stats">
        <div className="stat-item">
          <span className="stat-label">Total Logs:</span>
          <span className="stat-value">{totalCount}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Page:</span>
          <span className="stat-value">{page} of {totalPages}</span>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="loading-state">Loading logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">No cron job logs found</div>
      ) : (
        <div className="cron-table-container">
          <table className="cron-logs-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Summary</th>
                <th>Triggered By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.log_id}>
                  <td>
                    <div className="job-name">{log.job_name}</div>
                    <div className="job-id">{log.job_id}</div>
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(log.status)}>
                      {log.status}
                    </span>
                  </td>
                  <td className="datetime-cell">{formatDateTime(log.started_at)}</td>
                  <td>{formatDuration(log.duration_seconds)}</td>
                  <td className="summary-cell">
                    {log.summary || '-'}
                    {log.error_message && (
                      <div className="error-preview">❌ {log.error_message.substring(0, 50)}...</div>
                    )}
                  </td>
                  <td>
                    <span className="triggered-by-badge">{log.triggered_by}</span>
                  </td>
                  <td>
                    <button 
                      className="view-details-btn"
                      onClick={() => fetchLogDetail(log.log_id)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setPage(Math.max(1, page - 1))} 
            disabled={page === 1}
            className="pagination-btn"
          >
            ← Previous
          </button>
          <span className="page-info">Page {page} of {totalPages}</span>
          <button 
            onClick={() => setPage(Math.min(totalPages, page + 1))} 
            disabled={page === totalPages}
            className="pagination-btn"
          >
            Next →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="modal-overlay" onClick={closeDetailModal}>
          <div className="modal-content cron-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cron Job Execution Details</h3>
              <button className="close-btn" onClick={closeDetailModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h4>Basic Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Job Name:</span>
                    <span className="detail-value">{selectedLog.job_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Job ID:</span>
                    <span className="detail-value">{selectedLog.job_id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={getStatusBadgeClass(selectedLog.status)}>
                      {selectedLog.status}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Started At:</span>
                    <span className="detail-value">{formatDateTime(selectedLog.started_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Completed At:</span>
                    <span className="detail-value">{formatDateTime(selectedLog.completed_at)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Duration:</span>
                    <span className="detail-value">{formatDuration(selectedLog.duration_seconds)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Triggered By:</span>
                    <span className="detail-value">{selectedLog.triggered_by}</span>
                  </div>
                </div>
              </div>

              {selectedLog.summary && (
                <div className="detail-section">
                  <h4>Summary</h4>
                  <p className="summary-text">{selectedLog.summary}</p>
                </div>
              )}

              {selectedLog.details && (
                <div className="detail-section">
                  <h4>Execution Details</h4>
                  <pre className="json-details">{JSON.stringify(selectedLog.details, null, 2)}</pre>
                </div>
              )}

              {selectedLog.error_message && (
                <div className="detail-section error-section">
                  <h4>❌ Error Message</h4>
                  <div className="error-message">{selectedLog.error_message}</div>
                </div>
              )}

              {selectedLog.error_traceback && (
                <div className="detail-section error-section">
                  <h4>Stack Trace</h4>
                  <pre className="error-traceback">{selectedLog.error_traceback}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CronLogsTab;

